import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import {
  Mic,
  Video,
  Monitor,
  Zap,
  Globe,
  Code2,
  ShieldCheck,
  Webhook,
} from 'lucide-react'
import { fadeInUp, staggerContainer } from '@/lib/motion'
import { cn } from '@/lib/utils'

// All copy below verified against the codebase:
//   - "Sub-100ms" matches the existing /about page hero claim
//   - "300+" matches the existing About page ("300+ cities worldwide")
//   - "100 participants per room" matches the original FeaturesSection.Voice copy
//   - Atomic screen-share lock — confirmed via Redis SETNX in WebrtcRoomManager
//   - REST endpoints — POST /join, /publish, /subscribe wired in routes.rb
//   - HMAC SHA-256 webhook signing — confirmed in app/services/webhook_signature.rb
//   - TLS 1.3 + DTLS-SRTP — confirmed on /security page
const features = [
  {
    icon: Zap,
    title: 'Sub-100 ms latency',
    description:
      'Sessions route over a global edge network so glass-to-glass latency stays under 100 ms for end-users near a POP.',
    span: 'lg:col-span-2',
    accent: true,
  },
  {
    icon: Globe,
    title: '300+ edge POPs',
    description:
      'No region selection. Sessions converge on the closest POP for both publisher and subscriber.',
  },
  {
    icon: Mic,
    title: 'Voice',
    description:
      'Crystal-clear audio with adaptive bitrate. Up to 100 participants per room.',
  },
  {
    icon: Video,
    title: 'Video',
    description:
      'HD video with adaptive quality. Streams adjust to network conditions per participant.',
  },
  {
    icon: Monitor,
    title: 'Screen share',
    description:
      'Atomic Redis-locked presenter slot. Exactly one screen at a time, race-free.',
  },
  {
    icon: Code2,
    title: 'REST, no SDK',
    description:
      'POST /join, /publish, /subscribe over plain HTTPS. Generate clients from our OpenAPI spec.',
  },
  {
    icon: Webhook,
    title: 'Signed webhooks',
    description:
      'Room and participant events delivered with HMAC-SHA256 signatures over a timestamped payload.',
  },
  {
    icon: ShieldCheck,
    title: 'Encrypted by default',
    description:
      'TLS 1.3 for the API, DTLS-SRTP for media, bcrypt-hashed API secrets, instant key rotation.',
    span: 'lg:col-span-2',
    accent: true,
  },
]

export function FeaturesSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section
      id="features"
      aria-label="Features"
      className="relative py-28 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header — editorial */}
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-16 grid items-end gap-6 md:grid-cols-[auto_1fr] md:gap-12"
        >
          <motion.div variants={fadeInUp}>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              02 / capabilities
            </div>
            <h2 className="mt-3 max-w-xl font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              Everything You Need for Realtime
            </h2>
          </motion.div>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-muted-foreground md:max-w-md md:justify-self-end"
          >
            A complete set of APIs for building voice, video, and screen sharing
            into your application — without the complexity.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
        >
          {features.map((f) => (
            <motion.article
              key={f.title}
              variants={fadeInUp}
              className={cn(
                'surface-card surface-card-hover group relative overflow-hidden p-6 sm:p-7',
                f.span,
              )}
            >
              {/* Soft gradient corner — only on accent cards */}
              {f.accent && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full"
                  style={{
                    background:
                      'radial-gradient(circle, hsl(var(--brand-2) / 0.22) 0%, transparent 70%)',
                  }}
                />
              )}

              <div className="relative flex items-start gap-4">
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                    'bg-foreground/[0.04] text-foreground/80',
                    'group-hover:text-foreground transition-colors',
                  )}
                >
                  <f.icon className="h-4.5 w-4.5" strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-semibold tracking-tight">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.description}
                  </p>
                </div>
              </div>

              {/* Bottom hairline — gradient sheen on hover instead of a border */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-foreground/0 to-transparent transition-all duration-500 group-hover:via-[hsl(var(--brand-2)/0.6)]"
              />
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
